import { useEffect, useState } from 'react';
import { Building } from '../../confi-state';
import Rowhouse from './Rowhouse/Rowhouse';
import styles from './BuildingConf.module.css';
import Corridor from './Corridor/Corridor';
import Hybrid from './Hybrid/Hybrid';
import { buildingType } from './models/building-type.enum';
import Tower from './Tower/Tower';
import { TextField, Button, Alert, Stack } from '@mui/material';
import { config } from '../../app-confi/app-config';
import HybridRefined from './Hybrid/HybridRefined';
import CorridorRefined from './Corridor/CorridorRefined';
import TowerRefined from './Tower/TowerRefined';
import RowhouseRefined from './Rowhouse/RowhouseRefined';
import { ServiceManager } from '../../services/service-manager';
import { Point3 } from '../../common/models/point.type';
import { activeStateEnum, InitialProps, initialPropsStepEnum, InitProperties } from './Common/BuildingInitialPropertiesDialog/init-props';
import { UnitWish } from '../../models/unit-wish-list';
import { SectionJSON } from '../../models/section-json';
import { explanationIds } from '../../common/authentication/models/user';

const BuildingConf = ({ currentBuilding, buildingScale, initProps, previousBuildingDimensions, sectionJson, setBuildingInitProps, setOpenBuildingTypeModal, setPreviousBuildingDimensions, onChangeBuilding }: {
    currentBuilding: Building,
    buildingScale: Point3,
    initProps: InitialProps,
    previousBuildingDimensions: Point3,
    sectionJson: SectionJSON,
    setBuildingInitProps: (initProps: InitProperties) => void,
    setOpenBuildingTypeModal: (open: boolean) => void,
    setPreviousBuildingDimensions: (pt: Point3) => void,
    onChangeBuilding: (
        projectId: number | string,
        siteId: number | string,
        building: Building, saveToDb?: boolean
    ) => void
}) => {

    const [buildingName, setBuildingName] = useState('');

    const [nameError, setNameError] = useState<{
        error: boolean,
        message: string
    }>({ error: false, message: undefined });

    const validateName = (name: string) => {
        let valid = true;
        if (name.length < 1) {
            valid = false;
            setNameError({ error: true, message: 'Gebouw naam heeft geen karakters.' })
        } else if (name.length > config.shortFieldSize) {
            valid = false;
            setNameError({
                error: true, message: `Gebouw naam heeft meer dan ${config.shortFieldSize} karakters.`,
            })
        } else {
            setNameError({ error: false, message: undefined })
        }
        return valid;
    }

    useEffect(() => {
        validateName(currentBuilding.name);
        setBuildingName(currentBuilding.name);
    }, [currentBuilding.name])

    const getFormForBuildingType = () => {
        let temp;
        if (currentBuilding) {
            switch (currentBuilding.type) {
                case buildingType.hybrid:
                    temp = (<Hybrid currentBuilding={currentBuilding} previousBuildingDimensions={previousBuildingDimensions} buildingScaleDimensions={buildingScale} initProps={initProps} sectionJson={sectionJson} onChangeBuilding={onChangeBuilding}></Hybrid>);
                    break;
                case buildingType.tower:
                    temp = <Tower currentBuilding={currentBuilding} previousBuildingDimensions={previousBuildingDimensions} buildingScaleDimensions={buildingScale} initProps={initProps} onChangeBuilding={onChangeBuilding} sectionJson={sectionJson}></Tower>;
                    break;
                case buildingType.rowhouse:
                    temp = <Rowhouse currentBuilding={currentBuilding} previousBuildingDimensions={previousBuildingDimensions} buildingScaleDimensions={buildingScale} initProps={initProps} onChangeBuilding={onChangeBuilding}></Rowhouse>;
                    break;
                case buildingType.singleLoadedCorridor:
                    temp = (<Hybrid currentBuilding={currentBuilding} previousBuildingDimensions={previousBuildingDimensions} buildingScaleDimensions={buildingScale} initProps={initProps} sectionJson={sectionJson} onChangeBuilding={onChangeBuilding}></Hybrid>);
                    break;
                case buildingType.doubleLoadedCorridor:
                    temp = (<Corridor currentBuilding={currentBuilding} previousBuildingDimensions={previousBuildingDimensions} buildingScaleDimensions={buildingScale} initProps={initProps} onChangeBuilding={onChangeBuilding} sectionJson={sectionJson}></Corridor>);
                    break;
                default:
                    break;
            }
        }
        return temp;
    }

    const getRefineFormForBuildingType = () => {
        let temp;
        if (currentBuilding) {
            switch (currentBuilding.type) {
                case buildingType.hybrid:
                case buildingType.singleLoadedCorridor:
                    temp = (<HybridRefined currentBuilding={currentBuilding} onChangeBuilding={onChangeBuilding} sectionJson={sectionJson}></HybridRefined>);
                    break;
                case buildingType.doubleLoadedCorridor:
                    temp = (<CorridorRefined currentBuilding={currentBuilding} onChangeBuilding={onChangeBuilding} sectionJson={sectionJson}></CorridorRefined>);
                    break;
                case buildingType.tower:
                    temp = (<TowerRefined currentBuilding={currentBuilding} onChangeBuilding={onChangeBuilding} sectionJson={sectionJson}></TowerRefined>);
                    break;
                case buildingType.rowhouse:
                    temp = (<RowhouseRefined currentBuilding={currentBuilding} onChangeBuilding={onChangeBuilding} sectionJson={sectionJson}></RowhouseRefined>);
                    break;
                default:
                    break;
            }
        }
        return temp;
    }

    const handleRefine = (refine: boolean) => {
        const cb = { ...currentBuilding };
        if (refine) {
            cb.refined = true;
            ServiceManager.sceneManager.modelLoaderService.disableBoundingBoxGizmo(cb.id);
        } else {
            cb.refined = false;
            cb.props.partsRefined = [];
            ServiceManager.sceneManager.modelLoaderService.enableBoundingBoxGizmo(cb.id);
            ServiceManager.sceneManager.modelLoaderService.removeHighlights(cb.id)
        }
        onChangeBuilding(currentBuilding.projectId, currentBuilding.siteId, { ...cb }, true)
    }

    const handleChangeType = () => {
        setBuildingInitProps({ stepState: initialPropsStepEnum.buildingType, type: currentBuilding.type, unitWishList: [], state: activeStateEnum.edit })
        setOpenBuildingTypeModal(true);
    }

    const handleChangeUnitWishList = () => {
        setBuildingInitProps({ stepState: initialPropsStepEnum.livingMix, type: currentBuilding.type, unitWishList: [], editUnitWishList: currentBuilding.props.unitWishList as UnitWish[], state: activeStateEnum.editUnitWishList })
        setOpenBuildingTypeModal(true);
    }

    const startFromScratch = () => {
        const cb = { ...currentBuilding };
        if (cb.props) {
            setPreviousBuildingDimensions([cb.props.buildingLength, cb.props.buildingHeight, cb.props.buildingWidth])
        }
        cb.props = undefined;
        cb.refined = false;
        cb.selectedForDoc = false;
        cb.floorsForDoc = [];
        cb.deprecated = false;

        ServiceManager.sceneManager.modelLoaderService.enableBoundingBoxGizmo(cb.id);
        onChangeBuilding(currentBuilding.projectId, currentBuilding.siteId, { ...cb })
    }

    return (
        <div className={styles.ProjectConf} data-testid="BuildingConf">
            <div>
                <div>
                    {
                        !currentBuilding?.refined &&
                        <div className="group">
                            <h2 data-explanation-id={explanationIds.buildingEdit} className="greyed-out-text">Gebouw bewerken</h2>
                            {currentBuilding.name &&
                                <div className={styles.editInitProps}>
                                    <h2>{currentBuilding.name}</h2>
                                    <Button autoFocus={true} className={styles.buttons} variant="outlined" color="primary" onClick={() => handleChangeType()}>Gebouwtype Wijzigen</Button>
                                    <Button className={styles.buttons} variant="outlined" color="primary" onClick={() => handleChangeUnitWishList()}>Woonmix Aanpassen</Button>
                                </div>
                            }
                        </div>
                    }
                    {!currentBuilding.deprecated ?
                        <>
                            {currentBuilding.refined ? getRefineFormForBuildingType() : getFormForBuildingType()}
                        </> :
                        <div className="group">
                            <Stack sx={{ width: '100%' }} spacing={2}>
                                <Alert severity="error">Gebouw systeem veranderd.</Alert>
                                <Button color="primary" onClick={startFromScratch} variant="outlined">Opnieuw Genereren</Button>
                            </Stack>
                        </div>
                    }
                    {!currentBuilding.deprecated &&
                        <div className="group">
                            {currentBuilding.refined ?
                                <Button variant="outlined" color="primary" onClick={() => handleRefine(false)}>Gebouw Resetten</Button> :
                                <Button variant="outlined" color="primary" onClick={() => handleRefine(true)}>Gebouw Verfijnen</Button>
                            }
                        </div>
                    }
                </div>
            </div>
        </div>
    )
};

export default BuildingConf;
